import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getRequiredCurrentUserClientId } from "../../../core/auth/tenant";
import {
  buyPhoneNumber,
  getGreetingFileById,
  getGreetingUploadUrlById,
  getPurchasedPhoneNumbers,
  releasePhoneNumber,
  searchAvailablePhoneNumbers,
  uploadGreetingFileToPresignedUrl,
} from "../services/numbersService";

const getPlaceholderValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return String(value);
};

const formatMonthlyFee = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const stringValue = String(value).trim();
  if (!stringValue) {
    return "-";
  }

  const numericValue = Number(stringValue);
  if (Number.isFinite(numericValue)) {
    return `$${numericValue.toFixed(2)}`;
  }

  return stringValue;
};

const buildGreetingFileNameFromPhoneNumber = (phoneNumber) => {
  const digits = String(phoneNumber || "").replace(/\D/g, "");
  const normalizedDigits =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

  return normalizedDigits ? `${normalizedDigits}.mp3` : "";
};

const isLikelyTollFreeNumber = (phoneNumber = "", friendlyName = "") => {
  const normalized = String(phoneNumber).replace(/\D/g, "");
  const friendlyLower = String(friendlyName).toLowerCase();

  if (friendlyLower.includes("toll-free") || friendlyLower.includes("toll free")) {
    return true;
  }

  const tollFreePrefixes = ["800", "833", "844", "855", "866", "877", "888", "822"];

  if (normalized.length === 11 && normalized.startsWith("1")) {
    return tollFreePrefixes.some((prefix) => normalized.slice(1, 4) === prefix);
  }

  if (normalized.length === 10) {
    return tollFreePrefixes.some((prefix) => normalized.slice(0, 3) === prefix);
  }

  return false;
};

const mapAvailableNumber = (item, index) => {
  const capabilities = item?.capabilities || {};
  const phoneNumber = getPlaceholderValue(item?.phone_number || item?.phoneNumber);
  const friendlyName = getPlaceholderValue(item?.friendly_name || item?.friendlyName);
  const phoneType = "Local";

  return {
    id: item?.id || `${phoneNumber}-${index}`,
    number: phoneNumber,
    type: phoneType,
    monthlyFee: formatMonthlyFee(
      item?.monthly_price ?? item?.monthlyPrice ?? item?.monthly_fee ?? item?.monthlyFee,
    ),
    monthlyPrice: getPlaceholderValue(
      item?.monthly_price ?? item?.monthlyPrice ?? item?.monthly_fee ?? item?.monthlyFee,
    ),
    phoneNumber,
    friendlyName,
    phoneType,
    voiceCapabilities: Boolean(
      capabilities.voice ?? item?.voice_capabilities ?? item?.voiceCapabilities,
    ),
    smsCapabilities: Boolean(
      capabilities.SMS ??
        capabilities.sms ??
        item?.sms_capabilities ??
        item?.smsCapabilities,
    ),
    mmsCapabilities: Boolean(
      capabilities.MMS ??
        capabilities.mms ??
        item?.mms_capabilities ??
        item?.mmsCapabilities,
    ),
  };
};

const mapPurchasedNumber = (item, index) => {
  const greetingFile = getPlaceholderValue(
    item?.greetings_file_name || item?.greeting_file || item?.greetingFile,
  );
  const greetingUrl =
    item?.greetings_url ||
    item?.greeting_url ||
    item?.greetingsUrl ||
    item?.greetingUrl ||
    "";

  return {
    id: item?.id || `${item?.phone_number || item?.phoneNumber || "number"}-${index}`,
    phoneNumber: getPlaceholderValue(item?.phone_number || item?.phoneNumber),
    nickname: getPlaceholderValue(
      item?.friendly_name || item?.friendlyName || item?.nickname,
    ),
    greetingFile,
    greetingUrl,
    hasGreeting: greetingFile !== "-" || Boolean(greetingUrl),
  };
};

const extractGreetingUrl = (response) => {
  const data = response?.data || response;
  return (
    data?.greetings_url ||
    data?.greeting_url ||
    data?.greetingsUrl ||
    data?.greetingUrl ||
    ""
  );
};

const extractGreetingFileName = (greetingUrl) => {
  if (!greetingUrl) return "-";
  const sanitized = String(greetingUrl).split("?")[0];
  const name = sanitized.split("/").pop();
  return name || "-";
};

const getAvailableListFromResponse = (response) => {
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(response?.phone_numbers)) {
    return response.phone_numbers;
  }
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  return [];
};

const getPurchasedListFromResponse = (response) => {
  if (Array.isArray(response?.phone_numbers)) {
    return response.phone_numbers;
  }
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  if (Array.isArray(response)) {
    return response;
  }
  return [];
};

const getDynamicPageSize = (response, listLength = 0) => {
  const rawLimit = response?.limit ?? response?.page_size ?? response?.pageSize;
  const parsedLimit = Number(rawLimit);

  if (Number.isFinite(parsedLimit) && parsedLimit > 0) {
    return parsedLimit;
  }

  if (listLength > 0 && listLength < 10) {
    return 10;
  }

  return 10;
};

const getNumberManagementErrorMessage = (response, fallbackMessage) => {
  const rawMessage = (response?.error?.message || "").toString().trim();
  const loweredMessage = rawMessage.toLowerCase();

  const isGenericServerError =
    !rawMessage ||
    loweredMessage.includes("internal server error") ||
    loweredMessage.includes("server error") ||
    loweredMessage.includes("unexpected error");

  if (isGenericServerError) {
    return fallbackMessage;
  }

  return rawMessage;
};

export const fetchPurchasedNumbersThunk = createAsyncThunk(
  "numbers/fetchPurchasedNumbers",
  async (clientId, { getState, rejectWithValue }) => {
    const resolvedClientId = Number(clientId) || getRequiredCurrentUserClientId(getState());
    const response = await getPurchasedPhoneNumbers(resolvedClientId, {
      limit: 100,
      offset: 0,
    });

    if (response?.error) {
      return rejectWithValue(
        getNumberManagementErrorMessage(response, "Failed to load purchased numbers."),
      );
    }

    return getPurchasedListFromResponse(response).map(mapPurchasedNumber);
  },
);

export const uploadGreetingThunk = createAsyncThunk(
  "numbers/uploadGreeting",
  async ({ number, file }, { getState, rejectWithValue }) => {
    if (!number?.id) {
      return rejectWithValue("Phone number id is missing.");
    }

    const targetFileName = buildGreetingFileNameFromPhoneNumber(number.phoneNumber);
    if (!targetFileName) {
      return rejectWithValue("Phone number is missing. Unable to name greeting file.");
    }

    const resolvedClientId = getRequiredCurrentUserClientId(getState());
    const uploadResponse = await getGreetingUploadUrlById(number.id, {
      client_id: resolvedClientId,
    });

    if (uploadResponse?.error) {
      return rejectWithValue(
        getNumberManagementErrorMessage(uploadResponse, "Failed to request upload URL."),
      );
    }

    const uploadUrl =
      uploadResponse?.greetings_upload_url ||
      uploadResponse?.greeting_upload_url ||
      uploadResponse?.data?.greetings_upload_url ||
      uploadResponse?.data?.greeting_upload_url ||
      "";

    if (!uploadUrl) {
      return rejectWithValue("Upload URL was not returned by the server.");
    }

    let uploadResult;
    try {
      const fileToUpload =
        file?.name === targetFileName
          ? file
          : new File([file], targetFileName, { type: "audio/mpeg" });

      uploadResult = await uploadGreetingFileToPresignedUrl(uploadUrl, fileToUpload);
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to upload greeting file.");
    }

    if (!uploadResult?.ok) {
      let errorDetails = "";
      try {
        errorDetails = await uploadResult.text();
      } catch (error) {
        errorDetails = "";
      }

      return rejectWithValue(
        errorDetails
          ? `Upload failed: ${errorDetails}`
          : "Failed to upload greeting file.",
      );
    }

    const greetingResponse = await getGreetingFileById(number.id);
    if (greetingResponse?.error) {
      return rejectWithValue(
        getNumberManagementErrorMessage(greetingResponse, "Greeting uploaded but not available yet."),
      );
    }

    const greetingUrl = extractGreetingUrl(greetingResponse);
    const greetingFileFromUrl = extractGreetingFileName(greetingUrl);
    const resolvedGreetingUrl =
      greetingUrl && (!greetingFileFromUrl || greetingFileFromUrl === targetFileName)
        ? greetingUrl
        : "";
    const greetingFile = targetFileName || greetingFileFromUrl || "-";

    return {
      numberId: number.id,
      greetingUrl: resolvedGreetingUrl,
      greetingFile,
    };
  },
);

export const searchAvailableNumbersThunk = createAsyncThunk(
  "numbers/searchAvailableNumbers",
  async (_, { getState, rejectWithValue }) => {
    const state = getState().numbers;

    const patternMap = {
      first: "starts_with",
      last: "ends_with",
      anywhere: "contains",
    };

    const payload = {
      country_code: state.selectedCountry,
      search_type: "number",
      search_pattern: patternMap[state.matchTo] || "contains",
      search_string: (state.searchDigits || "").trim(),
    };

    const response = await searchAvailablePhoneNumbers(payload);

    if (response?.error) {
      return rejectWithValue(
        getNumberManagementErrorMessage(response, "Failed to search phone numbers."),
      );
    }

    const numbers = getAvailableListFromResponse(response)
      .map((item, index) => mapAvailableNumber(item, index))
      .filter((item) => !isLikelyTollFreeNumber(item.phoneNumber, item.friendlyName));

    return {
      numbers,
      availablePageSize: getDynamicPageSize(response, numbers.length),
    };
  },
);

export const buyNumberThunk = createAsyncThunk(
  "numbers/buyNumber",
  async ({ number, clientId }, { dispatch, getState, rejectWithValue }) => {
    const resolvedClientId = Number(clientId) || getRequiredCurrentUserClientId(getState());
    if (!number || !number.phoneNumber || number.phoneNumber === "-") {
      return rejectWithValue("Unable to buy this number because phone number is missing.");
    }

    const payload = {
      client_id: resolvedClientId,
      phone_number: number.phoneNumber,
      phone_type: "local",
      friendly_name:
        number.friendlyName === "-" ? number.phoneNumber : number.friendlyName,
      voice_capabilities: Boolean(number.voiceCapabilities),
      sms_capabilities: Boolean(number.smsCapabilities),
      mms_capabilities: Boolean(number.mmsCapabilities),
    };

    const response = await buyPhoneNumber(payload);

    if (response?.error) {
      return rejectWithValue(
        getNumberManagementErrorMessage(response, "Failed to buy and assign phone number"),
      );
    }

    await dispatch(fetchPurchasedNumbersThunk(resolvedClientId));

    return { phoneNumber: number.phoneNumber };
  },
);

export const releaseNumberThunk = createAsyncThunk(
  "numbers/releaseNumber",
  async ({ number, clientId }, { dispatch, getState, rejectWithValue }) => {
    const resolvedClientId = Number(clientId) || getRequiredCurrentUserClientId(getState());
    if (!number || !number.phoneNumber || number.phoneNumber === "-") {
      return rejectWithValue("Unable to release this number because phone number is missing.");
    }

    const response = await releasePhoneNumber(number.phoneNumber);

    if (response?.error) {
      return rejectWithValue(
        getNumberManagementErrorMessage(response, "Failed to release phone number."),
      );
    }

    await dispatch(fetchPurchasedNumbersThunk(resolvedClientId));
    return { success: true };
  },
);

const initialState = {
  selectedCountry: "US",
  searchCriteria: "number",
  searchDigits: "",
  matchTo: "",
  availableNumbers: [],
  availablePageSize: 10,
  purchasedNumbers: [],
  availableLoading: false,
  purchasedLoading: false,
  actionLoading: false,
  availableSpread: false,
  purchasedSpread: false,
  error: null,
};

export const numbersInitialState = initialState;

const numbersSlice = createSlice({
  name: "numbers",
  initialState,
  reducers: {
    setSelectedCountry: (state, action) => {
      state.selectedCountry = action.payload;
    },
    setSearchCriteria: (state, action) => {
      state.searchCriteria = action.payload;
    },
    setSearchDigits: (state, action) => {
      state.searchDigits = action.payload;
    },
    setMatchTo: (state, action) => {
      state.matchTo = action.payload;
    },
    resetSearchForm: (state) => {
      state.selectedCountry = "US";
      state.searchCriteria = "number";
      state.searchDigits = "";
      state.matchTo = "";
      state.availableNumbers = [];
      state.availablePageSize = 10;
    },
    toggleAvailableSpread: (state) => {
      state.availableSpread = !state.availableSpread;
    },
    togglePurchasedSpread: (state) => {
      state.purchasedSpread = !state.purchasedSpread;
    },
    uploadGreetingLocally: (state, action) => {
      const { numberId, fileName } = action.payload;
      state.purchasedNumbers = state.purchasedNumbers.map((item) =>
        item.id === numberId
          ? { ...item, greetingFile: fileName, hasGreeting: true }
          : item,
      );
    },
    updateGreetingMetadata: (state, action) => {
      const { numberId, greetingUrl, greetingFile } = action.payload;
      state.purchasedNumbers = state.purchasedNumbers.map((item) =>
        item.id === numberId
          ? {
              ...item,
              greetingUrl: greetingUrl || item.greetingUrl,
              greetingFile: greetingFile || item.greetingFile,
              hasGreeting: Boolean(greetingUrl || greetingFile || item.greetingFile),
            }
          : item,
      );
    },
    clearNumbersError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPurchasedNumbersThunk.pending, (state) => {
        state.purchasedLoading = true;
        state.error = null;
      })
      .addCase(fetchPurchasedNumbersThunk.fulfilled, (state, action) => {
        state.purchasedLoading = false;
        state.purchasedNumbers = action.payload;
      })
      .addCase(fetchPurchasedNumbersThunk.rejected, (state, action) => {
        state.purchasedLoading = false;
        state.error = action.payload || "Failed to load purchased numbers.";
      })
      .addCase(searchAvailableNumbersThunk.pending, (state) => {
        state.availableLoading = true;
        state.error = null;
      })
      .addCase(searchAvailableNumbersThunk.fulfilled, (state, action) => {
        state.availableLoading = false;
        state.availableNumbers = action.payload.numbers;
        state.availablePageSize = action.payload.availablePageSize;
      })
      .addCase(searchAvailableNumbersThunk.rejected, (state, action) => {
        state.availableLoading = false;
        state.error = action.payload || "Failed to search phone numbers.";
      })
      .addCase(buyNumberThunk.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(buyNumberThunk.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.availableNumbers = state.availableNumbers.filter(
          (item) => item.phoneNumber !== action.payload.phoneNumber,
        );
      })
      .addCase(buyNumberThunk.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Failed to buy and assign phone number";
      })
      .addCase(releaseNumberThunk.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(releaseNumberThunk.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(releaseNumberThunk.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Failed to release phone number.";
      })
      .addCase(uploadGreetingThunk.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(uploadGreetingThunk.fulfilled, (state, action) => {
        state.actionLoading = false;
        const { numberId, greetingUrl, greetingFile } = action.payload || {};
        state.purchasedNumbers = state.purchasedNumbers.map((item) =>
          item.id === numberId
            ? {
                ...item,
                greetingUrl: greetingUrl || "",
                greetingFile: greetingFile || item.greetingFile,
                hasGreeting: Boolean(greetingUrl || greetingFile),
              }
            : item,
        );
      })
      .addCase(uploadGreetingThunk.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Failed to upload greeting file.";
      });
  },
});

export const {
  clearNumbersError,
  resetSearchForm,
  setMatchTo,
  setSearchCriteria,
  setSearchDigits,
  setSelectedCountry,
  toggleAvailableSpread,
  togglePurchasedSpread,
  uploadGreetingLocally,
  updateGreetingMetadata,
} = numbersSlice.actions;

export const selectNumbersState = (state) => state.numbers;

export default numbersSlice.reducer;
